import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, AlertTriangle, Mail, User as UserIcon, Lock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pickedRole, setPickedRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/invite/${token}`)
      .then(r => {
        setInvite(r.data);
        if (r.data.email) setEmail(r.data.email);
        // If the admin didn't fix a role, default the picker to the first allowed role
        if (!r.data.role && r.data.allowed_roles?.length) {
          setPickedRole(r.data.allowed_roles[0]);
        }
      })
      .catch(err => setError(err.response?.data?.detail || "Invite link is invalid"))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!name.trim() || !email.trim() || password.length < 6) {
      setSubmitError("Fill all fields — password must be at least 6 characters");
      return;
    }
    if (!invite.role && !pickedRole) {
      setSubmitError("Please pick a role");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { name, email, password };
      if (!invite.role) payload.role = pickedRole;
      const r = await axios.post(`${API_URL}/api/invite/${token}/accept`, payload);
      localStorage.setItem("ysv_token", r.data.token);
      localStorage.setItem("ysv_user", JSON.stringify(r.data.user));
      setAccepted(true);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || "Failed to accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={pageStyle}><div style={cardStyle}><p style={{ color: "#888", textAlign: "center" }}>Loading invite…</p></div></div>;
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle} data-testid="invite-error">
          <AlertTriangle size={44} style={{ color: "#ff9800", margin: "0 auto 16px", display: "block" }} />
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 700, textAlign: "center", marginBottom: 8 }}>Invite Unavailable</h1>
          <p style={{ color: "#888", fontSize: 14, textAlign: "center", marginBottom: 24 }}>{error}</p>
          <button onClick={() => navigate("/login")} style={primaryBtn} data-testid="invite-error-login">Go to Login</button>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle} data-testid="invite-accepted">
          <CheckCircle size={52} style={{ color: "var(--cyan)", margin: "0 auto 18px", display: "block" }} />
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 700, textAlign: "center", marginBottom: 8 }}>You're in!</h1>
          <p style={{ color: "#fff", fontSize: 14, textAlign: "center", marginBottom: 8 }}>
            Welcome, <strong>{name}</strong>.
          </p>
          <div style={{ background: "rgba(255,152,0,0.1)", border: "1px solid rgba(255,152,0,0.3)", borderRadius: 10, padding: "14px 16px", margin: "20px 0" }}>
            <p style={{ fontSize: 13, color: "#ff9800", fontWeight: 600, marginBottom: 4 }}>Awaiting Approval</p>
            <p style={{ fontSize: 12, color: "#cc8a00" }}>{invite.invited_by_name} will approve your account shortly. You'll have full access once approved.</p>
          </div>
          <button onClick={() => navigate("/home")} style={primaryBtn} data-testid="invite-accepted-continue">Continue</button>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle} data-testid="invite-page">
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: "var(--cyan)", textTransform: "uppercase", marginBottom: 6 }}>YSV Invite</p>
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 8 }}>You've been invited</h1>
          <p style={{ color: "#aaa", fontSize: 14 }}>
            <strong style={{ color: "#fff" }}>{invite.invited_by_name}</strong> invited you to join
          </p>
          <p data-testid="invite-scope" style={{ color: "var(--cyan)", fontSize: 14, fontWeight: 600, marginTop: 4 }}>{invite.scope_label}</p>
          {invite.role ? (
            <p style={{ display: "inline-block", marginTop: 10, background: "rgba(255,107,0,0.12)", color: "#ff6b00", fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.08em" }}>{invite.role.replace("_", " ")}</p>
          ) : null}
        </div>

        {!invite.role && (invite.allowed_roles?.length || 0) > 0 && (
          <div data-testid="invite-role-picker" style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: "#888", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8, display: "block" }}>I'm joining as</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {invite.allowed_roles.map(r => (
                <button
                  key={r}
                  type="button"
                  data-testid={`invite-role-${r}`}
                  onClick={() => setPickedRole(r)}
                  style={{
                    flex: 1, minWidth: 120,
                    padding: "12px 14px",
                    background: pickedRole === r ? "var(--cyan)" : "#0f141d",
                    color: pickedRole === r ? "#000" : "#fff",
                    border: `1px solid ${pickedRole === r ? "var(--cyan)" : "#253040"}`,
                    borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                    textTransform: "capitalize",
                  }}
                >
                  {r.replace("_", "/")}
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={accept}>
          <Field icon={UserIcon} placeholder="Full name" value={name} onChange={setName} testid="invite-name" />
          <Field icon={Mail} placeholder="Email" type="email" value={email} onChange={setEmail} testid="invite-email" />
          <Field icon={Lock} placeholder="Password (min 6 chars)" type="password" value={password} onChange={setPassword} testid="invite-password" />

          {submitError && <p data-testid="invite-submit-error" style={{ color: "#f44336", fontSize: 13, marginBottom: 12 }}>{submitError}</p>}

          <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.7 : 1 }} data-testid="invite-submit-btn">
            {submitting ? "Creating account…" : "Accept Invite & Register"}
          </button>
        </form>

        <p style={{ color: "#666", fontSize: 11, textAlign: "center", marginTop: 18 }}>
          After registering, your account waits for approval from {invite.invited_by_name} before app access is unlocked.
        </p>
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, placeholder, type = "text", value, onChange, testid, readOnly }) => (
  <div style={{ position: "relative", marginBottom: 12 }}>
    <Icon size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#666" }} />
    <input
      data-testid={testid}
      type={type}
      placeholder={placeholder}
      value={value}
      readOnly={readOnly}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%", padding: "14px 14px 14px 42px",
        background: readOnly ? "#0a0f17" : "#0f141d",
        border: "1px solid #253040",
        borderRadius: 10, color: "#fff", fontSize: 14,
        fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box",
      }}
    />
  </div>
);

const pageStyle = {
  minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20, background: "linear-gradient(135deg, #0a1628 0%, #0f1a2f 100%)",
};
const cardStyle = {
  background: "#1a2230", border: "1px solid #253040", borderRadius: 16,
  padding: 32, width: "100%", maxWidth: 440,
};
const primaryBtn = {
  width: "100%", padding: 14, background: "var(--cyan)", color: "#000",
  border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700,
  cursor: "pointer", fontFamily: "'Outfit', sans-serif",
};

export default InvitePage;
